import { Theme, alpha } from '@mui/material/styles';
import { buttonClasses } from '@mui/material/Button';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

// Date-only pickers (closeOnSelect, no action bar)
const dateOnlyList = ['DatePicker', 'StaticDatePicker', 'DesktopDatePicker', 'MobileDatePicker'];

// DateTime pickers (action bar with accept only, no cancel)
const dateTimeList = ['DateTimePicker', 'DesktopDateTimePicker', 'MobileDateTimePicker'];

const timeList = ['TimePicker', 'MobileTimePicker', 'StaticTimePicker', 'DesktopTimePicker'];

const switchIcon = () => <Iconify icon="eva:chevron-down-fill" width={24} />;

const leftIcon = () => <Iconify icon="eva:arrow-ios-back-fill" width={24} />;

const rightIcon = () => <Iconify icon="eva:arrow-ios-forward-fill" width={24} />;

const calendarIcon = () => <Iconify icon="solar:calendar-mark-bold-duotone" width={24} />;

const clockIcon = () => <Iconify icon="solar:clock-circle-outline" width={24} />;

export function datePicker(theme: Theme) {
  const commonSlots = {
    openPickerIcon: calendarIcon,
    leftArrowIcon: leftIcon,
    rightArrowIcon: rightIcon,
    switchViewIcon: switchIcon,
  };

  const customSlotProps = {
    textField: {
      size: 'small' as const,
      sx: {
        '& .MuiInputBase-input': {
          fontSize: '14px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: alpha(theme.palette.grey[500], 0.2),
        },
      },
    },
  };

  // Date-only pickers: close on select, no action bar
  const dateOnlyTypes = dateOnlyList.reduce((result: Record<string, any>, currentValue) => {
    result[`Mui${currentValue}`] = {
      defaultProps: {
        closeOnSelect: true,
        slots: commonSlots,
        slotProps: customSlotProps,
      },
    };
    return result;
  }, {});

  // DateTime pickers: show accept button only (no cancel)
  const dateTimeTypes = dateTimeList.reduce((result: Record<string, any>, currentValue) => {
    result[`Mui${currentValue}`] = {
      defaultProps: {
        slots: commonSlots,
        slotProps: {
          ...customSlotProps,
          actionBar: {
            actions: ['accept'],
          },
        },
      },
    };
    return result;
  }, {});

  const timeTypes = timeList.reduce((result: Record<string, any>, currentValue) => {
    result[`Mui${currentValue}`] = {
      defaultProps: {
        closeOnSelect: true,
        slots: {
          openPickerIcon: clockIcon,
          rightArrowIcon: rightIcon,
          switchViewIcon: switchIcon,
        },
        slotProps: customSlotProps,
      },
    };
    return result;
  }, {});

  return {
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          '& .MuiPickersLayout-actionBar': {
            [`& .${buttonClasses.root}:last-of-type`]: {
              backgroundColor: theme.palette.text.primary,
              color: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.grey[800],
            },
          },
        },
      },
    },

    // PickersTextField overrides (v9 uses PickersTextField instead of regular TextField)
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          minWidth: 0,
        },
      },
    },

    // Match font to regular MUI TextField
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          ...theme.typography.body2,
          minWidth: 0,
        },
        input: {
          ...theme.typography.body2,
          '&::placeholder': {
            opacity: 1,
            color: theme.palette.text.disabled,
          },
        },
        sectionsContainer: {
          lineHeight: '22px',
        },
        activeBar: {
          backgroundColor: `${theme.palette.text.primary} !important`,
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: any }) => ({
          height: ownerState?.inputSize === 'small' || ownerState?.size === 'small' ? 40 : 56,
          minWidth: 0,
          '&.MuiPickersInputBase-inputSizeSmall': {
            height: '40px !important',
          },
          '& .MuiPickersInputBase-inputSizeSmall': {
            height: '40px !important',
          },
          '& .MuiInputBase-inputSizeSmall': {
            height: '40px !important',
          },
          '& .MuiOutlinedInput-inputSizeSmall': {
            height: '40px !important',
          },
          '& .MuiOutlinedInput-sizeSmall': {
            height: '40px !important',
          },
          '& .MuiInputBase-sizeSmall': {
            height: '40px !important',
          },
          '& .MuiOutlinedInput-root.Mui-focused': {
            borderColor: `${theme.palette.text.primary} !important`,
          },
          '& .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.grey[500], 0.2),
            transition: theme.transitions.create(['border-color'], {
              duration: theme.transitions.duration.shortest,
            }),
          },
          '&:hover .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.text.primary} !important`,
          },
          '&.MuiPickersOutlinedInput-focused .MuiPickersOutlinedInput-notchedOutline, &.MuiPickersInputBase-focused .MuiPickersOutlinedInput-notchedOutline, &.Mui-focused .MuiPickersOutlinedInput-notchedOutline, &:focus-within .MuiPickersOutlinedInput-notchedOutline':
            {
              borderColor: `${theme.palette.text.primary} !important`,
            },
          '&.MuiPickersOutlinedInput-focused:not(.MuiPickersOutlinedInput-error) .MuiPickersOutlinedInput-notchedOutline, &.MuiPickersInputBase-focused:not(.MuiPickersInputBase-error) .MuiPickersOutlinedInput-notchedOutline':
            {
              borderColor: `${theme.palette.text.primary} !important`,
            },
          '&.Mui-error .MuiPickersOutlinedInput-notchedOutline, &.MuiPickersOutlinedInput-error .MuiPickersOutlinedInput-notchedOutline, &.MuiPickersInputBase-error .MuiPickersOutlinedInput-notchedOutline':
            {
              borderColor: `${theme.palette.error.main} !important`,
            },
          '&.Mui-disabled .MuiPickersOutlinedInput-notchedOutline, &.MuiPickersOutlinedInput-disabled .MuiPickersOutlinedInput-notchedOutline, &.MuiPickersInputBase-disabled .MuiPickersOutlinedInput-notchedOutline':
            {
              borderColor: `${theme.palette.action.disabledBackground} !important`,
            },
        }),
        notchedOutline: {
          borderColor: alpha(theme.palette.grey[500], 0.2),
          transition: theme.transitions.create(['border-color'], {
            duration: theme.transitions.duration.shortest,
          }),
        },
      },
    },

    // Date-only pickers
    ...dateOnlyTypes,

    // DateTime pickers
    ...dateTimeTypes,

    // Time pickers
    ...timeTypes,
  };
}
